import React from 'react';
import { AgFinancingFile } from '@indigo-ag/schema';
import { palettes } from '@indigo-ag/component-module';
import { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import i18next from 'core/i18n';

import { createStylesHook } from 'shared/hooks/createStylesHook';
import { utils, Document } from 'shared';
import { UserUtil } from 'shared/utils';

const useStyles = createStylesHook({
  documentsContainer: {
    marginBottom: 8,
    width: '100%',
    '& > div:not(:first-child)': {
      marginTop: 16
    }
  },
  noStyledButton: {
    border: 'none',
    background: 'none',
    padding: 0,
    margin: 0
  },
  anchor: {
    color: palettes.coastal.core.mediumBlue,
    cursor: 'pointer'
  },
  textTransformCapitalize: {
    textTransform: 'capitalize'
  }
});

interface DocumentsListProps {
  documents: AgFinancingFile[];
  hideDocumentDetails?: boolean;
  hideDocuments?: boolean;
  displayViewAll?: boolean;
  maxDocumentsToDisplay?: number;
  onViewAllDocumentsClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  icon?: IconDefinition;
  onIconClick?: (document: AgFinancingFile) => void;
  isVisible?: boolean;
}

export const DocumentsList: React.FunctionComponent<DocumentsListProps> = ({
  documents,
  hideDocumentDetails = false,
  hideDocuments = false,
  displayViewAll = false,
  maxDocumentsToDisplay = 1,
  onViewAllDocumentsClick,
  icon,
  onIconClick,
  isVisible
}) => {
  /**
   * Styles
   */
  const classes = useStyles();

  const docsToDisplay: AgFinancingFile[] = hideDocuments ? documents.slice(0, maxDocumentsToDisplay) : documents;
  const notifyIconClicked = (document: AgFinancingFile) => {
    return onIconClick ? () => onIconClick(document) : undefined;
  };

  return (
    <>
      <div className={classes.documentsContainer}>
        {docsToDisplay.map(d => (
          <Document
            key={d.id}
            documentTitle={d.documentName}
            documentUrl={d.documentUrl}
            content={
              !hideDocumentDetails
                ? `${UserUtil.getUserFullName(d.uploadedBy)} · ${utils.date.formatDate(d.loadedAt)}`
                : undefined
            }
            icon={icon}
            onIconClick={notifyIconClicked(d)}
            comment={d.metadata?.comment}
            isVisible={isVisible}
          />
        ))}
      </div>
      {hideDocuments && (documents.length > maxDocumentsToDisplay || displayViewAll) ? (
        <button
          onClick={onViewAllDocumentsClick}
          className={`${classes.noStyledButton} ${classes.anchor} ${classes.textTransformCapitalize}`}
        >
          {i18next.t('viewAllDocuments')}
        </button>
      ) : null}
    </>
  );
};
